import type { DrawingModuleType } from "../DrawingSingleton";
import type { DrawingModule } from "../types/DrawingWeb";
import type { HatchProp, DrawingEntityProps } from "../types/NoteConfig";
import { deleteObj } from "../core/delete";
import { openAs, getCurrentSpaceId } from "../core/oda";
import {
  OpenMode,
  HatchPatternType,
  HatchPatternTypeClosure,
  HatchLoopType,
} from "../core/constants";
import { applyEntityProps } from "../entities/properties";
import { DR_HATCHES } from "../../hatches/hatch-list";
import { PAT_LIBRARY } from "../../hatches/pat-library";
import { HatchReactorManager } from "../managers/HatchReactorManager";
import { Logger } from "@/logger";

/**
 * Known predefined hatch patterns available in ODA.
 * These are standard AutoCAD patterns built into the library.
 */
export const PREDEFINED_PATTERNS = [
  "SOLID",
  "ANGLE",
  "ANSI31",
  "ANSI32",
  "ANSI33",
  "ANSI34",
  "ANSI35",
  "ANSI36",
  "ANSI37",
  "ANSI38",
  "AR-B816",
  "AR-B816C",
  "AR-B88",
  "AR-BRELM",
  "AR-BRSTD",
  "AR-CONC",
  "AR-HBONE",
  "AR-PARQ1",
  "AR-RROOF",
  "AR-RSHKE",
  "AR-SAND",
  "BOX",
  "BRASS",
  "BRICK",
  "BRSTONE",
  "CLAY",
  "CORK",
  "CROSS",
  "DASH",
  "DOLMIT",
  "DOTS",
  "EARTH",
  "ESCHER",
  "FLEX",
  "GRASS",
  "GRATE",
  "GRAVEL",
  "HEX",
  "HONEY",
  "HOUND",
  "INSUL",
  "LINE",
  "MUDST",
  "NET",
  "NET3",
  "PLAST",
  "PLASTI",
  "SACNCR",
  "SQUARE",
  "STARS",
  "STEEL",
  "SWAMP",
  "TRANS",
  "TRIANG",
  "ZIGZAG",
] as const;

export type PredefinedPatternName = (typeof PREDEFINED_PATTERNS)[number];

// Cache for tested pattern availability
const patternAvailabilityCache = new Map<string, boolean>();

/**
 * Test if a predefined hatch pattern is available in ODA.
 * Results are cached for performance.
 *
 * FIXME : PREDEFINED_PATTERNS could be used here but for the time we keep this
 * safe check at runtime. ODA Should provide a list of available pattern as a
 * source of truth
 *
 * @param module - The drawing module
 * @param db - The database
 * @param patternName - Name of the pattern to test
 * @returns true if the pattern is available
 */
export function isPatternAvailable(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
  patternName: string,
): boolean {
  // Check cache first
  if (patternAvailabilityCache.has(patternName)) {
    return patternAvailabilityCache.get(patternName)!;
  }

  let available = false;

  try {
    const hatch = module.OdDbHatch.createObject();
    hatch.setDatabaseDefaults(db, true);

    const name = new module.OdString();
    name.set(patternName);

    // Try to set the pattern - this will fail if pattern doesn't exist
    hatch.setPattern(HatchPatternType.kPreDefined as any, name, 0);

    deleteObj(name);
    deleteObj(hatch);

    available = true;
  } catch (error) {
    console.warn(
      `[Hatch] Pattern "${patternName}" is not available as predefined:`,
      error,
    );
    available = false;
  }

  // Cache the result
  patternAvailabilityCache.set(patternName, available);
  return available;
}

/**
 * Get a list of available predefined patterns by testing each one.
 * This can be slow on first call as it tests all patterns.
 *
 * @param module - The drawing module
 * @param db - The database
 * @returns Array of available pattern names
 */
export function getAvailablePredefinedPatterns(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
): string[] {
  const available: string[] = [];

  for (const pattern of PREDEFINED_PATTERNS) {
    if (isPatternAvailable(module, db, pattern)) {
      available.push(pattern);
    }
  }

  return available;
}

/**
 * Clear the pattern availability cache.
 * Useful if switching to a different database.
 */
export function clearPatternCache(): void {
  patternAvailabilityCache.clear();
}

/**
 * Find a custom pattern by name in DR_HATCHES.
 */
function findCustomPattern(
  patternName: string,
): { name: string; baseScale?: number } | undefined {
  return DR_HATCHES.find(
    (p) => p.name.toUpperCase() === patternName.toUpperCase(),
  );
}

/**
 * Get list of available custom pattern names from DR_HATCHES.
 */
export function getCustomPatternNames(): string[] {
  return DR_HATCHES.map((p) => p.name);
}

// Cache of pattern names already written to the virtual FS so we only write once.
const writtenPatternFiles = new Set<string>();

function applyCustomPattern(
  module: DrawingModuleType,
  hatch: DrawingModule.OdDbHatch,
  customPattern: { name: string },
  hatchConfig: HatchProp,
): boolean {
  try {
    const patName = customPattern.name;
    const patContent = PAT_LIBRARY[patName];

    if (!patContent) {
      console.warn(
        `[Hatch] Custom pattern "${patName}" not found in PAT_LIBRARY`,
      );
      return false;
    }

    // OdHatchPattern (OdArray<OdHatchPatternLine>) is not constructible from JS,
    // and patternManager.appendPattern is not bound in the WASM module.
    // Write the original .pat content to Emscripten's virtual FS so ODA can locate
    // it via findFile(kPatternFile) when evaluateHatch() is called with kCustomDefined.
    if (!writtenPatternFiles.has(patName)) {
      const fs: { writeFile(path: string, data: string): void } = (
        window as any
      ).FS;
      const dir = "/";
      try {
        fs.writeFile(`${dir}${patName}.pat`, patContent);
      } catch {
        Logger.error(
          `hatch.ts : could not write pat file ${patName}.pat to FS`,
        );
      }
      writtenPatternFiles.add(patName);
    }

    const patternName = new module.OdString();
    patternName.set(patName);

    hatch.setPattern(
      HatchPatternType.kCustomDefined as any,
      patternName,
      HatchPatternTypeClosure.kNoneClosure as any,
    );

    deleteObj(patternName);

    const scale = hatchConfig.scale ?? 1;
    if (scale > 0) {
      hatch.setPatternScale(scale);
    }

    if (hatchConfig.rotation !== undefined && hatchConfig.rotation !== 0) {
      const rotationAngle = (hatchConfig.rotation * Math.PI) / 180;
      hatch.setPatternAngle(rotationAngle);
    }

    return true;
  } catch (error) {
    console.error(
      `[Hatch] Failed to apply custom pattern "${customPattern.name}":`,
      error,
    );
    return false;
  }
}

/**
 * Apply a hatch pattern to a hatch entity.
 * Tries in order:
 * 1. Predefined pattern (if available)
 * 2. Custom pattern from DR_HATCHES
 * 3. Falls back to SOLID
 *
 * @param module - The drawing module
 * @param db - The database
 * @param hatch - The hatch entity to configure
 * @param hatchConfig - Hatch pattern configuration
 * @returns The actual pattern name used (may differ from requested if fallback occurred)
 */
function applyHatchPattern(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
  hatch: DrawingModule.OdDbHatch,
  hatchConfig: HatchProp,
): string {
  // Cast to string since HatchName enum may not include all predefined patterns like 'SOLID'
  const requestedPattern = hatchConfig.name as string;

  // SOLID is always available as predefined
  if (requestedPattern === "SOLID") {
    const patternName = new module.OdString();
    patternName.set("SOLID");
    hatch.setPattern(HatchPatternType.kPreDefined as any, patternName, 0);
    deleteObj(patternName);
    return "SOLID";
  }

  // Try predefined pattern first
  if (isPatternAvailable(module, db, requestedPattern)) {
    const patternName = new module.OdString();
    patternName.set(requestedPattern);
    hatch.setPattern(HatchPatternType.kPreDefined as any, patternName, 0);
    deleteObj(patternName);

    // Apply scale and rotation for predefined patterns
    if (hatchConfig.scale !== undefined && hatchConfig.scale > 0) {
      hatch.setPatternScale(hatchConfig.scale);
    }
    if (hatchConfig.rotation !== undefined) {
      const angleRadians = (hatchConfig.rotation * Math.PI) / 180;
      hatch.setPatternAngle(angleRadians);
    }

    return requestedPattern;
  }

  // Try custom pattern from DR_HATCHES
  const customPattern = findCustomPattern(requestedPattern);
  if (customPattern) {
    if (applyCustomPattern(module, hatch, customPattern, hatchConfig)) {
      return requestedPattern;
    }
  }

  // Fall back to SOLID
  console.warn(
    `[Hatch] Pattern "${requestedPattern}" not available, falling back to SOLID`,
  );
  const patternName = new module.OdString();
  patternName.set("SOLID");
  hatch.setPattern(HatchPatternType.kPreDefined as any, patternName, 0);
  deleteObj(patternName);
  return "SOLID";
}

/**
 * Result of creating a hatch entity.
 */
export interface HatchEntityResult {
  entityId: DrawingModule.OdDbObjectId;
  hatch: DrawingModule.OdDbHatch;
}

/**
 * Create a hatch entity from a boundary loop of 2D points.
 * The hatch is added to model space.
 *
 * @param module - The drawing module
 * @param db - The database
 * @param points - Array of 2D points forming the closed boundary
 * @param hatchConfig - Hatch pattern configuration
 * @param entityProps - Optional entity properties (color, layer, etc.)
 * @returns The created hatch entity and its ID
 */
export function createHatchFromPoints(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
  points: Array<{ x: number; y: number }>,
  hatchConfig: HatchProp,
  entityProps?: DrawingEntityProps,
): HatchEntityResult | null {
  // Create hatch entity
  const hatch = module.OdDbHatch.createObject();
  hatch.setDatabaseDefaults(db, true);

  // Apply pattern with fallback
  applyHatchPattern(module, db, hatch, hatchConfig);

  // Create boundary loop from points
  const vertices = new module.OdGePoint2dArray();
  const bulges = new module.OdGeDoubleArray();

  for (const point of points) {
    const pt = new module.OdGePoint2d(point.x, point.y);
    vertices.push_back(pt);
    bulges.push_back(0); // No arc segments (straight lines)
    deleteObj(pt);
  }

  // Append loop as external boundary
  hatch.appendLoop1(
    HatchLoopType.kExternal as any,
    vertices,
    bulges,
    HatchPatternTypeClosure.kSolidClosure,
  );

  deleteObj(vertices);
  deleteObj(bulges);

  // Evaluate hatch to generate geometry. ODA throws a native exception for a
  // boundary it can't fill (self-intersecting, degenerate/zero-area, etc.), which
  // happens routinely on freehand-drawn shapes. Treat that as "no fill" rather
  // than letting it crash the whole interaction.
  try {
    hatch.evaluateHatch(true);
  } catch (error) {
    Logger.captureException(error, {
      operation: "createHatchFromPoints",
      pointCount: points.length,
      patternName: hatchConfig.name,
    });
    deleteObj(hatch);
    return null;
  }

  // Append to current space
  const currentSpaceId = getCurrentSpaceId(db);
  const currentSpace = openAs(
    currentSpaceId,
    module.OdDbBlockTableRecord,
    OpenMode.kForWrite,
  );
  const entityId = currentSpace.appendOdDbEntity(hatch);

  deleteObj(currentSpace);

  // Apply entity properties if provided
  if (entityProps) {
    applyEntityProps(module, db, entityId, entityProps);
  }

  return { entityId, hatch };
}

/**
 * Create a hatch entity from an existing closed entity (circle, ellipse, closed polyline).
 * The hatch is associated with the boundary entity.
 *
 * @param module - The drawing module
 * @param db - The database
 * @param boundaryEntityId - ID of the closed entity to use as boundary
 * @param hatchConfig - Hatch pattern configuration
 * @param entityProps - Optional entity properties (color, layer, etc.)
 * @returns The created hatch entity and its ID
 */
export function createHatchFromEntity(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
  boundaryEntityId: DrawingModule.OdDbObjectId,
  hatchConfig: HatchProp,
  entityProps?: DrawingEntityProps,
): HatchEntityResult | null {
  // Create hatch entity
  const hatch = module.OdDbHatch.createObject();
  hatch.setDatabaseDefaults(db, true);

  // Apply pattern with fallback
  applyHatchPattern(module, db, hatch, hatchConfig);

  // Create object ID array for the boundary entity
  const entityIds = new module.OdDbObjectIdArray();
  entityIds.push_back(boundaryEntityId);

  // Append loop from entity
  // Using appendLoop2 which takes entity IDs
  hatch.appendLoop2(
    HatchLoopType.kExternal as any,
    entityIds,
    HatchPatternTypeClosure.kSolidClosure,
  );

  deleteObj(entityIds);

  // Evaluate hatch to generate geometry. ODA throws a native exception for a
  // boundary it can't fill (self-intersecting, degenerate/zero-area, etc.), which
  // happens routinely on freehand-drawn or manually edited shapes. Treat that as
  // "no fill" rather than letting it crash the whole interaction.
  try {
    hatch.evaluateHatch(true);
  } catch (error) {
    Logger.captureException(error, {
      operation: "createHatchFromEntity",
      patternName: hatchConfig.name,
    });
    deleteObj(hatch);
    return null;
  }

  // Append to current space
  const currentSpaceId = getCurrentSpaceId(db);
  const currentSpace = openAs(
    currentSpaceId,
    module.OdDbBlockTableRecord,
    OpenMode.kForWrite,
  );
  const entityId = currentSpace.appendOdDbEntity(hatch);

  deleteObj(currentSpace);

  // Apply entity properties if provided
  if (entityProps) {
    applyEntityProps(module, db, entityId, entityProps);
  }

  // Register hatch as a persistent reactor on the boundary entity
  HatchReactorManager.getInstance(module).registerReactor(
    boundaryEntityId,
    entityId,
  );

  return { entityId, hatch };
}

/**
 * Create a solid fill hatch (no pattern, just solid color).
 *
 * @param module - The drawing module
 * @param db - The database
 * @param points - Array of 2D points forming the closed boundary
 * @param entityProps - Optional entity properties (color, layer, etc.)
 * @returns The created hatch entity and its ID
 */
export function createSolidFill(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
  points: Array<{ x: number; y: number }>,
  entityProps?: DrawingEntityProps,
): HatchEntityResult | null {
  return createHatchFromPoints(
    module,
    db,
    points,
    { name: "SOLID" as any },
    entityProps,
  );
}

/**
 * Create a hatch for a rectangle boundary.
 *
 * @param module - The drawing module
 * @param db - The database
 * @param x1 - First corner X
 * @param y1 - First corner Y
 * @param x2 - Opposite corner X
 * @param y2 - Opposite corner Y
 * @param hatchConfig - Hatch pattern configuration
 * @param entityProps - Optional entity properties
 * @returns The created hatch entity and its ID
 */
export function createRectangleHatch(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  hatchConfig: HatchProp,
  entityProps?: DrawingEntityProps,
): HatchEntityResult | null {
  const points = [
    { x: x1, y: y1 },
    { x: x2, y: y1 },
    { x: x2, y: y2 },
    { x: x1, y: y2 },
  ];

  return createHatchFromPoints(module, db, points, hatchConfig, entityProps);
}
